import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import AuthModal from "./AuthModal";
import Toast from "./Toast";

function Navbar({ cart = [] }) {
  const token = localStorage.getItem("token");
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const [authMode, setAuthMode] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") !== "light"
  );
  const [scrolled, setScrolled] = useState(false);
  const [showSignoutToast, setShowSignoutToast] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleThemeToggle = () => {
    const root = document.documentElement;
    const next = root.getAttribute("data-theme") === "light" ? "dark" : "light";
    root.setAttribute("data-theme", next);
    localStorage.setItem("theme", next);
    setIsDark(next === "dark");
  };

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSectionNav = (e, sectionId) => {
    e.preventDefault();
    setMenuOpen(false);
    if (pathname === "/") {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: sectionId } });
    }
  };

  const openAuth = (mode) => { setMenuOpen(false); setAuthMode(mode); };

  return (
    <>
      <header className={`n2${scrolled ? " scrolled" : ""}`}>
        <div className="n2-inner">

          {/* Logo */}
          <Link to="/" className="n2-logo">
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="TableCure Logo"
                className="n2-logo-icon"
                loading="eager"
              />
            </picture>
          </Link>

          {/* Nav links */}
          <ul className="n2-links">
            <li><Link to="/" className="n2-link">Shop</Link></li>
            <li><a href="#collection" className="n2-link" onClick={(e) => handleSectionNav(e, "collection")}>Collection</a></li>
            <li><a href="#about" className="n2-link" onClick={(e) => handleSectionNav(e, "about")}>About</a></li>
          </ul>

          {/* Actions */}
          <div className="n2-actions">
            <Link to="/cart" className="n2-cart" aria-label={`Cart, ${totalItems} items`}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {totalItems > 0 && (
                <span className="n2-badge">{totalItems > 9 ? "9+" : totalItems}</span>
              )}
            </Link>

            <div className="n2-sep" />

            <button className="n2-theme" onClick={handleThemeToggle}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}>
              {isDark ? (
                <svg viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  width="24" height="24" style={{width: "24px", height: "24px", flexShrink: 0, transform: "scale(0.5)"}}>
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  width="24" height="24" style={{width: "24px", height: "24px", flexShrink: 0, transform: "scale(0.5)"}}>
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                </svg>
              )}
            </button>

            <div className="n2-sep" />

            {token ? (
              <>
                <Link to="/dashboard" className="n2-profile">Profile</Link>
                <button className="n2-signout"
                  onClick={() => setShowSignoutToast(true)}>
                  Sign out
                </button>
              </>
            ) : (
              <>
                <button className="n2-signin" onClick={() => openAuth("login")}>Sign in</button>
                <button className="n2-register" onClick={() => openAuth("register")}>Register</button>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button className={`n2-ham${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span className="n2-ham-line"/>
            <span className="n2-ham-line"/>
            <span className="n2-ham-line"/>
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div className={`n2-overlay${menuOpen ? " open" : ""}`}>
        <Link to="/" className="n2-ov-link" onClick={() => setMenuOpen(false)}>Shop</Link>
        <a href="#collection" className="n2-ov-link" onClick={(e) => handleSectionNav(e, "collection")}>Collection</a>
        <a href="#about" className="n2-ov-link" onClick={(e) => handleSectionNav(e, "about")}>About</a>
        <div className="n2-ov-actions">
          <Link to="/cart" className="n2-ov-btn" onClick={() => setMenuOpen(false)}>
            Cart {totalItems > 0 && `(${totalItems})`}
          </Link>
          {token ? (
            <>
              <Link to="/dashboard" className="n2-ov-btn" onClick={() => setMenuOpen(false)}>Profile</Link>
              <button className="n2-ov-btn"
                onClick={() => setShowSignoutToast(true)}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <button className="n2-ov-btn" onClick={() => openAuth("login")}>Sign in</button>
              <button className="n2-ov-btn" onClick={() => openAuth("register")}>Register</button>
            </>
          )}
          <button className="n2-ov-btn" onClick={handleThemeToggle}>
            {isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          </button>
        </div>
      </div>

      {authMode && <AuthModal initialMode={authMode} onClose={() => setAuthMode(null)} />}

      <Toast
        show={showSignoutToast}
        onClose={() => setShowSignoutToast(false)}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        variant="danger"
        autoClose={0}
        position="top-center"
        actions={[
          { label: "Sign Out", variant: "danger", onClick: () => { localStorage.removeItem("token"); window.location.reload(); } },
          { label: "Cancel", variant: "ghost", onClick: () => setShowSignoutToast(false) },
        ]}
      />
    </>
  );
}

export default Navbar;
